import { useState, useEffect, useCallback, useMemo } from "react";
import HttpClient, { Methods } from "../../../../services/HttpClient";
import { formatDisplayDate } from "../../../../util/dateFormat";
import { Button, Box, IconButton, TextField } from "@mui/material";
import { OverviewHeaderBar } from "../../../common/OverviewHeaderBar";
import { ScrollableDataGrid } from "../../../common/ScrollableDataGrid";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../constants/AppRoutes";
import { endpoints } from "../../../../constants/Endpoints";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import RefreshIcon from "@mui/icons-material/Refresh";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteIcon from "@mui/icons-material/Delete";

export function LeadArchiveTable() {
  const styles = {
    exportToCsvButton: {
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": { color: "#d01717" },
    },
    exportToCsvLink: { color: "white", textDecoration: "none" },
    refreshButton: {
      backgroundColor: "#f68121",
      color: "#ffffff",
      "&:hover": { color: "#eb008b" },
    },
  };

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [isMounted, setMounted] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.lead.getArchivedRentalPriceCalculations,
      null
    );
    return await response.json();
  }, []);

  const refreshData = () => {
    setLoading(true);
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });
  };

  const renewRentalPriceCalculation = async (id) => {
    setLoading(true);
    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.lead.renewRentalPriceCalculation + id
    );
    const message = await response.json();
    if (response.ok) {
      Swal.fire({
        toast: true,
        timer: 3000,
        position: "bottom-end",
        showConfirmButton: false,
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    const updated = await getData();
    setData(updated);
    setLoading(false);
  };

  const permanentlyDeleteRentalPriceCalculation = async (id) => {
    setLoading(true);
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.lead.deleteRentalPriceCalculation + id
    );
    let message = null;
    try {
      message = await response.json();
    } catch (_) {}
    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
      Toast.fire({ icon: "success", title: "Aanvraag permanent verwijderd" });
    } else {
      Swal.fire({
        icon: message?.icon || "error",
        title: message?.title || "Verwijderen mislukt",
        text:
          message?.content ||
          "De huurprijsaanvraag kon niet permanent worden verwijderd.",
      });
    }
    const updated = await getData();
    setData(updated);
    setLoading(false);
  };

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });
    return () => setMounted(false);
  }, [isMounted, getData]);

  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Acties",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 0.5,
            }}
          >
            <IconButton
              size="small"
              title="Vernieuw huurprijsaanvraag"
              onClick={(e) => {
                e.stopPropagation();
                Swal.fire({
                  title: "Huurprijsaanvraag vernieuwen",
                  text: "Wil je de huurprijsaanvraag vernieuwen?",
                  icon: "info",
                  showCancelButton: true,
                  confirmButtonColor: "#eb008b",
                  confirmButtonText: "Vernieuw aanvraag",
                }).then((result) => {
                  if (result.value) renewRentalPriceCalculation(params.row.id);
                });
              }}
            >
              <RestoreIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              title="Permanent verwijderen"
              onClick={(e) => {
                e.stopPropagation();
                Swal.fire({
                  title: "Permanent verwijderen",
                  text: "Weet je zeker dat je deze huurprijsaanvraag permanent wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#ec2027",
                  confirmButtonText: "Verwijderen",
                  cancelButtonText: "Annuleren",
                }).then((res) => {
                  if (res.isConfirmed) {
                    permanentlyDeleteRentalPriceCalculation(params.row.id);
                  }
                });
              }}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Box>
        ),
      },
      { field: "street", headerName: "Straat", flex: 1, minWidth: 160 },
      { field: "number", headerName: "Nummer", width: 90 },
      {
        field: "leadApplicationUserSalutation",
        headerName: "Aanhef",
        width: 120,
      },
      {
        field: "leadApplicationUserFirstName",
        headerName: "Voornaam",
        width: 150,
      },
      {
        field: "leadApplicationUserLastName",
        headerName: "Achternaam",
        width: 170,
      },
      {
        field: "leadApplicationUserEmail",
        headerName: "E-mail",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "leadApplicationUserPhoneNumber",
        headerName: "Telefoonnummer",
        width: 160,
      },
      {
        field: "leadRegistrationDate",
        headerName: "Datum aanvraag",
        width: 160,
        renderCell: (p) => {
          const value = p?.row?.leadRegistrationDate;
          if (!value) return <span>-</span>;
          let formatted = formatDisplayDate(value) || "-";
          return <span>{formatted}</span>;
        },
      },
    ],
    []
  );

  const csvHeaders = [
    { label: "Datum aanvraag", key: "leadRegistrationDate" },
    { label: "Aanvraag bevestigd", key: "leadIsConfirmed" },
    { label: "Aanhef", key: "leadApplicationUserSalutation" },
    { label: "Achternaam", key: "leadApplicationUserLastName" },
    { label: "Voornaam", key: "leadApplicationUserFirstName" },
    { label: "Telefoonnummer", key: "leadApplicationUserPhoneNumber" },
    { label: "Email", key: "leadApplicationUserEmail" },
    { label: "Is eigenaar", key: "isOwner" },
    { label: "Aantal slaapkamers", key: "numberOfBedrooms" },
    { label: "Aantal m2", key: "surfaceInSquareMeters" },
    { label: "Oplevering", key: "interior" },
    { label: "Straat", key: "street" },
    { label: "Huisnummer", key: "houseNumber" },
    { label: "Toevoeging", key: "addition" },
    { label: "Postcode", key: "zipCode" },
    { label: "Stad", key: "city" },
    { label: "Extra info", key: "extraInfo" },
    { label: "Voorgestelde huurprijs", key: "proposedRentalPrice" },
    { label: "Voorgesteld op", key: "dateRentalPriceProposedOn" },
    { label: "Eigen notities", key: "SHBNotes" },
  ];

  const filteredRows = useMemo(() => {
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(
      (r) =>
        r.leadApplicationUserFirstName?.toLowerCase().includes(s) ||
        r.leadApplicationUserLastName?.toLowerCase().includes(s) ||
        r.leadApplicationUserEmail?.toLowerCase().includes(s) ||
        r.street?.toLowerCase().includes(s)
    );
  }, [data, search]);

  const onRowClick = (params) => {
    navigate(
      appRoutes.shb.lead.rentalPriceCalculationArchiveDetail + params.row.id
    );
  };

  return (
    <Box>
      <OverviewHeaderBar
        title="Huurprijsaanvragen archief"
        actions={
          <>
            <TextField
              size="small"
              label="Zoeken"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <CSVLink
              data={data}
              style={styles.exportToCsvLink}
              headers={csvHeaders}
              filename="archief_huurprijscalculatie.csv"
            >
              <Button
                variant="contained"
                startIcon={<SystemUpdateAltIcon />}
                style={styles.exportToCsvButton}
              >
                Download CSV
              </Button>
            </CSVLink>
            <Button
              variant="contained"
              style={styles.refreshButton}
              startIcon={<RefreshIcon />}
              onClick={refreshData}
            >
              <span>Vernieuw</span>
            </Button>
          </>
        }
      />
      <Box sx={{ mx: 3, mb: 3 }}>
        <ScrollableDataGrid
          rows={(Array.isArray(filteredRows) ? filteredRows : []).filter(
            (r) => r && r.id !== null && r.id !== undefined
          )}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
          onRowClick={onRowClick}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          dataGridSx={{ backgroundColor: "background.paper" }}
        />
      </Box>
    </Box>
  );
}
